import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';

import {
	IInvoiceDate,
	inquiryInvoice,
	useBusinessLayer,
	useInvoicesDates,
	UserActionTypes,
} from '@mopla/business-logic';

const RATE_LIMIT_COOLDOWN_MS = 60_000;

export const useDownloadInvoices = () => {
	const bl = useBusinessLayer();
	const { invoicesDatesList, isLoading } = useInvoicesDates();
	const [isRateLimited, setIsRateLimited] = useState(false);
	const rateLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Get available years from invoices data
	const availableYears = useMemo(() => {
		if (!invoicesDatesList) return [];
		return Object.keys(invoicesDatesList)
			.map(Number)
			.sort((a, b) => b - a); // Sort descending (newest first)
	}, [invoicesDatesList]);

	// Default to most recent year or current year
	const [selectedYear, setSelectedYear] = useState<number>(() => {
		if (availableYears.length > 0) {
			return availableYears[0];
		}
		return new Date().getFullYear();
	});

	// Update selected year when data loads
	useEffect(() => {
		if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
			setSelectedYear(availableYears[0]);
		}
	}, [availableYears, selectedYear]);

	// Get invoices for selected year
	const invoicesForYear = useMemo(() => {
		if (!invoicesDatesList || !invoicesDatesList[selectedYear]) return [];
		// Sort by month ascending (oldest first)
		return [...invoicesDatesList[selectedYear]].sort(
			(a, b) => a.month - b.month
		);
	}, [invoicesDatesList, selectedYear]);

	// Create tabs from available years
	const tabs = useMemo(() => {
		return availableYears.map((year) => ({
			label: year.toString(),
			value: year,
		}));
	}, [availableYears]);

	const handleYearChange = useCallback((value: string | number) => {
		setSelectedYear(Number(value));
	}, []);

	const startRateLimitTimer = useCallback(() => {
		// Clear any existing timer so the notification stays visible
		// until the LAST clicked invoice's cooldown expires
		if (rateLimitTimerRef.current) {
			clearTimeout(rateLimitTimerRef.current);
		}

		setIsRateLimited(true);

		rateLimitTimerRef.current = setTimeout(() => {
			setIsRateLimited(false);
			rateLimitTimerRef.current = null;
		}, RATE_LIMIT_COOLDOWN_MS);
	}, []);

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (rateLimitTimerRef.current) {
				clearTimeout(rateLimitTimerRef.current);
			}
		};
	}, []);

	const sendInvoice = useCallback(
		(invoice: IInvoiceDate) => {
			return new Promise<void>((resolve, reject) => {
				bl.dispatch(
					inquiryInvoice({
						subscriptionId: invoice.subscriptionId,
						month: invoice.month,
						year: invoice.year,
					})
				);

				bl.watchActions({
					types: UserActionTypes.InquiryInvoiceResult,
					callback: (action) => {
						if (action.payload?.error) {
							const error = action.payload.error;
							const isRateLimitError =
								isAxiosError(error) &&
								error.response?.data?.code === 'invoice_already_sent';

							if (isRateLimitError) {
								startRateLimitTimer();
							}

							reject(error);
						} else {
							resolve();
						}
					},
				});
			});
		},
		[bl, startRateLimitTimer]
	);

	return {
		isLoading,
		isRateLimited,
		tabs,
		selectedYear,
		invoicesForYear,
		handleYearChange,
		sendInvoice,
	};
};
